import { useEffect, useState } from 'react'

// Create / edit a promotion. Mirrors the SupplierEditModal /
// StaffEditModal pattern.
//
// Props:
//   open            — boolean
//   onClose         — fn
//   onSave(id, patch)   — required in edit mode
//   onCreate(payload)   — required in create mode
//   onDelete(id)        — optional; shown when promo is provided
//   promotion       — null/undef = create mode; otherwise edit
const CHANNEL_OPTIONS = [
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook',  label: 'Facebook' },
  { id: 'sms',       label: 'SMS' },
  { id: 'email',     label: 'Email' },
  { id: 'in_store',  label: 'In-store' },
]

export default function PromotionEditModal({
  open,
  onClose,
  onSave,
  onCreate,
  onDelete,
  promotion,
}) {
  const isCreateMode = !promotion
  const [form, setForm]           = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]         = useState(null)

  useEffect(() => {
    if (!open) return
    if (promotion) {
      setForm({
        name:          promotion.name ?? '',
        description:   promotion.description ?? '',
        starts_on:     promotion.starts_on ?? today(),
        ends_on:       promotion.ends_on ?? '',
        channels:      Array.isArray(promotion.channels) ? promotion.channels : [],
        cost_estimate: promotion.cost_estimate != null ? String(promotion.cost_estimate) : '',
        status:        promotion.status ?? 'planned',
        notes:         promotion.notes ?? '',
        style_notes:   promotion.style_notes ?? '',
      })
    } else {
      setForm({
        name: '',
        description: '',
        starts_on: today(),
        ends_on: '',
        channels: ['instagram', 'facebook'],
        cost_estimate: '',
        status: 'planned',
        notes: '',
        style_notes: '',
      })
    }
    setError(null)
    setSubmitting(false)
  }, [open, promotion])

  if (!open || !form) return null

  function set(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function toggleChannel(channel) {
    setForm((prev) => {
      const has = prev.channels.includes(channel)
      return {
        ...prev,
        channels: has
          ? prev.channels.filter((c) => c !== channel)
          : [...prev.channels, channel],
      }
    })
  }

  async function handleSubmit(e) {
    e?.preventDefault?.()
    if (submitting) return
    if (!form.name.trim()) {
      setError('Name is required.')
      return
    }
    if (!form.starts_on) {
      setError('Start date is required.')
      return
    }
    if (form.ends_on && form.ends_on < form.starts_on) {
      setError('End date must be on or after start date.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const payload = {
        name:          form.name.trim(),
        description:   form.description.trim() || null,
        starts_on:     form.starts_on,
        ends_on:       form.ends_on || null,
        channels:      form.channels,
        cost_estimate: form.cost_estimate !== '' ? Number(form.cost_estimate) : null,
        status:        form.status,
        notes:         form.notes.trim() || null,
        style_notes:   form.style_notes.trim() || null,
      }
      if (isCreateMode) {
        await onCreate(payload)
      } else {
        await onSave(promotion.id, payload)
      }
      onClose?.()
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.error('[PromotionEditModal] save failed', e2)
      setError(`Could not save: ${e2?.message || 'try again.'}`)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDelete() {
    if (!promotion || !onDelete) return
    const ok = window.confirm(
      `Delete "${promotion.name}"?\n\nThis removes the promotion permanently. Continue?`
    )
    if (!ok) return
    setSubmitting(true)
    try {
      await onDelete(promotion.id)
      onClose?.()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[PromotionEditModal] delete failed', e)
      setError(`Could not delete: ${e?.message || 'try again.'}`)
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <form
        onSubmit={handleSubmit}
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label={isCreateMode ? 'New promotion' : `Edit ${promotion.name}`}
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium truncate">
              {isCreateMode ? 'New promotion' : `Edit · ${promotion.name}`}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-4 px-4 pb-4">
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="promo-name">Name</label>
            <input
              id="promo-name"
              type="text"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder='e.g. "Memorial Day spend-and-save"'
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="promo-desc">Description</label>
            <textarea
              id="promo-desc"
              rows={3}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder='e.g. "Spend $60 get $20 off, dine-in only, all weekend"'
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
            <p className="mt-1 text-xs text-black/55">
              The AI uses this verbatim — be specific about the offer, the dates, and any restrictions.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="promo-start">Starts</label>
              <input
                id="promo-start"
                type="date"
                value={form.starts_on}
                onChange={(e) => set('starts_on', e.target.value)}
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base font-medium tabular-nums outline-none focus:border-blue-600"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="promo-end">Ends (optional)</label>
              <input
                id="promo-end"
                type="date"
                value={form.ends_on}
                onChange={(e) => set('ends_on', e.target.value)}
                min={form.starts_on}
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base font-medium tabular-nums outline-none focus:border-blue-600"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Channels</label>
            <div className="flex flex-wrap gap-2">
              {CHANNEL_OPTIONS.map((c) => {
                const active = form.channels.includes(c.id)
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleChannel(c.id)}
                    className={`rounded-full px-3 py-1.5 text-sm font-medium border ${
                      active
                        ? 'border-blue-600 bg-blue-600 text-white'
                        : 'border-black/15 bg-white text-black/65 hover:bg-blue-50'
                    }`}
                  >
                    {c.label}
                  </button>
                )
              })}
            </div>
            <p className="mt-1 text-xs text-black/55">
              The AI tailors output for each selected channel.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="promo-cost">
                Cost estimate ($)
              </label>
              <input
                id="promo-cost"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={form.cost_estimate}
                onChange={(e) => set('cost_estimate', e.target.value)}
                placeholder="e.g. 200"
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base font-medium tabular-nums outline-none focus:border-blue-600"
              />
              <p className="mt-1 text-xs text-black/55">
                Optional. Estimated discount cost or ad spend.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="promo-status">Status</label>
              <select
                id="promo-status"
                value={form.status}
                onChange={(e) => set('status', e.target.value)}
                className="w-full rounded-lg border border-black/15 bg-white px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              >
                <option value="planned">Planned</option>
                <option value="live">Live</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="promo-style">
              Style notes (optional)
            </label>
            <input
              id="promo-style"
              type="text"
              value={form.style_notes}
              onChange={(e) => set('style_notes', e.target.value)}
              placeholder='e.g. "casual + Tex-Mex slang", "formal, family-friendly"'
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
            <p className="mt-1 text-xs text-black/55">
              Voice / tone hint for the AI. Leave blank for friendly + casual default.
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="promo-notes">Internal notes (optional)</label>
            <textarea
              id="promo-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Anything you want to remember about this promo"
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
          </div>

          {!isCreateMode && onDelete && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={submitting}
              className="w-full rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 active:bg-red-100 disabled:opacity-50"
            >
              Delete promotion
            </button>
          )}
        </div>

        <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
          <button
            type="submit"
            disabled={submitting}
            className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Saving\u2026' : (isCreateMode ? 'Save promotion' : 'Save changes')}
          </button>
        </div>
      </form>
    </div>
  )
}

function today() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
